import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/shared/widgets/accordion";
import { Input } from "@/shared/widgets/input";
import type { AccountMergingResponse } from "../../application/services/accountMergingService";
import { CheckCircle2 } from "lucide-react";
import { useIsMobile } from "@/core/hooks/useMediaQuery";

interface Props {
  phoneNumber: string;
  accountsData: AccountMergingResponse["customerAccountsData"];
  primaryEmail: string;
  onBack: () => void;
  secondaryActions: Record<string, string>;
  onActionSelect: (email: string, action: string) => void;
  transferInputs: Record<string, string>;
  onTransferInputChange: (email: string, value: string) => void;
  transferErrors: Record<string, string>;
  verifiedTransfers: Record<string, { phoneNumber: string; otpId: number }>;
  onRequestOtp: (phoneNumber: string, type: "re-assign", email: string) => void;
  isOtpLoading: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
  isSubmitDisabled: boolean;
  getAvatarColor: (index: number) => string;
}

const ACTION_CHIP_STYLES: Record<string, string> = {
  "merge": "bg-[#e6f4ea] text-[#1e8e3e]",
  "discard": "bg-[#FEF1F0] text-[#DF190B]",
  "re-assign": "bg-[#f9f5ff] text-[#6941c6]",
};

const RADIO_OPTIONS = [
  {
    value: "merge",
    title: "Merge into primary account",
    description: "Orders, Blue Cash, Blue Credits and other benefits will move to your primary account",
  },
  {
    value: "discard",
    title: "Discard",
    description: "You will not be able to access this account once it is removed",
  },
  {
    value: "re-assign",
    title: "Assign to a new number",
    description:"",
  },
];

export function AccountMergingSecondaryScreen({
  phoneNumber,
  accountsData,
  primaryEmail,
  onBack,
  secondaryActions,
  onActionSelect,
  transferInputs,
  onTransferInputChange,
  transferErrors,
  verifiedTransfers,
  onRequestOtp,
  isOtpLoading,
  getAvatarColor,
  isSubmitting,
  onSubmit,
  isSubmitDisabled,
}: Props) {

  const secondaryAccounts = Object.entries(accountsData).filter(([email]) => email !== primaryEmail);
  const isMobile = useIsMobile();
  
  return (
    <>
      <div>
        <p className="text-xl font-semibold mb-[10px]">
          Manage other accounts
        </p>

        <p className={`text-gray-600 ${isMobile ? "text-sm" : ""}`}>
          Choose how would you like to handle other accounts associated with the phone 
          number <strong className="text-black">{phoneNumber}</strong>
        </p>
      </div>

      <Accordion type="single" collapsible
        className="flex-1 overflow-y-auto flex flex-col gap-3 py-3"
      >
        {secondaryAccounts.map(([email, details], index) => {
          const currentAction = secondaryActions[email];
          const inputNum = transferInputs[email] || "";
          
          const verifiedData = verifiedTransfers[email];
          const isCurrentInputVerified = Boolean(verifiedData) && inputNum === verifiedData.phoneNumber;

          let showChip = false;
          if (currentAction) {
            if (currentAction === "re-assign") {
              showChip = isCurrentInputVerified;
            } else {
              showChip = true;
            }
          }

          return (
            <AccordionItem
              key={email}
              value={email}
              className={`border border-gray-200 bg-white ${isMobile ? "py-[12px]" : " py-[15px]"}`}
            >
              <AccordionTrigger className="hover:no-underline py-0 px-[20px] overflow-hidden">
                <div className="flex items-center gap-4 text-[#1a1a4b] w-full pr-4 min-w-0">
                  <div
                    className="w-[36px] h-[36px] rounded-full flex items-center justify-center text-white text-md flex-shrink-0"
                    style={{ backgroundColor: getAvatarColor(index) }}
                  >
                    {email.charAt(0).toUpperCase()}
                  </div>

                  <span className="font-semibold truncate text-sm flex-1 text-left min-w-0">
                    {email}
                  </span>

                  {showChip && currentAction && (
                    <span className={`px-3 py-1 text-[11px] font-bold rounded-sm capitalize tracking-wide shrink-0 ${ACTION_CHIP_STYLES[currentAction]}`}>
                      {currentAction}
                    </span>
                  )}
                </div>
              </AccordionTrigger>

              <AccordionContent className="pb-0">
                <div className="flex flex-col gap-4 p-[20px] mt-[15px] border-t pb-2">
                  
                  {RADIO_OPTIONS.map((option) => {
                    if (option.value === "discard" && !details.isAllowedToDiscard) {
                      return null;
                    }

                    const isChecked = currentAction === option.value;

                    return (
                      <div key={option.value} className="flex flex-col gap-3">
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <input
                            type="radio"
                            name={`action-${email}`}
                            value={option.value}
                            checked={isChecked}
                            onChange={() => onActionSelect(email, option.value)}
                            className="hidden"
                          />
                          <div className={`w-5 h-5 mt-0.5 shrink-0 rounded-full border-2 flex items-center justify-center transition
                            ${isChecked ? "border-[#F15D47] bg-[#F15D47]" : "border-gray-300 bg-white"}`}
                          >
                            {isChecked && <div className="w-2 h-2 bg-white rounded-full" />}
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-black">
                              {option.title}
                            </span>
                            {option.description && (
                              <span className="text-xs text-gray-500">
                                {option.description}
                              </span>
                            )}
                          </div>
                        </label>

                        {/* RE-ASSIGN INPUT BOX */}
                        {option.value === "re-assign" && isChecked && (
                          <div className={`flex flex-col gap-1 mt-1 ${isMobile ? "":"pl-8"}`}>
                            <div className="flex items-center gap-2">
                            <div className="flex items-center border border-gray-300 overflow-hidden bg-white w-full">
                              <span className={`text-sm text-gray-500 ${isMobile ? "px-1 pl-2 py-2" : "py-3 px-3"}`}>
                                +91
                              </span>
                              <Input
                                placeholder="Enter a new number"
                                value={inputNum}
                                onChange={(e) => onTransferInputChange(email, e.target.value)}
                                className="border-0 focus-visible:ring-0 h-9 rounded-none"
                              />
                              {isCurrentInputVerified && (
                                <CheckCircle2 className="w-6 h-6 mr-2" color="white" fill="green"/>
                              )}
                              {!isMobile && transferErrors[email] && (
                                <span className="p-3 text-xs text-red-500 font-medium whitespace-nowrap">
                                  {transferErrors[email]}
                                </span>
                              )}
                            </div>
                            
                            <button
                              type="button"
                              disabled={inputNum.length !== 10 || isCurrentInputVerified || isOtpLoading}
                              onClick={() => onRequestOtp(inputNum, "re-assign", email)}
                              className={`h-full text-sm font-medium text-white flex items-center transition-colors
                                ${isMobile ? "py-2 px-3" : "px-[40px] py-3"}
                                ${isCurrentInputVerified ? 'bg-[#fbcec7]' : 'bg-hover disabled:opacity-50 disabled:cursor-not-allowed'}`}
                            >
                              {isCurrentInputVerified ? (
                                <>Verified</>
                              ) : (
                                "Verify"
                              )}
                            </button>
                            </div>
                            {isMobile && transferErrors[email] && (
                              <span className="p-0 text-xs text-red-500 font-medium">
                                {transferErrors[email]}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      <div className="flex-none pt-4 flex gap-3">
        <button
          onClick={onBack}
          className="w-[100px] p-[14px] text-md border border-gray-300 text-black font-medium hover:bg-gray-300 transition-colors"
        >
          Back
        </button>
        <button
          disabled={isSubmitDisabled || isSubmitting}
          onClick={onSubmit} 
          className="w-full p-[14px] text-md bg-hover text-white font-medium hover:opacity-90 transition-opacity disabled:text-[#a0a0a0] disabled:bg-[#e0e0e0] disabled:cursor-not-allowed"
        >
          Submit
        </button>
      </div>
    </>
  );
}